import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const hybridParallelComponent: ComponentDef = {
  id: 'hybrid-parallel',
  name: 'Hybrid Parallel',
  description: 'Combine data, tensor, pipeline, and expert parallelism in a single training setup.',
  category: 'parallelism',
  params: [
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    { name: 'dp', label: 'DP size', type: 'number', default: 2, min: 1, max: 128, step: 1 },
    { name: 'tp', label: 'TP size', type: 'number', default: 2, min: 1, max: 16, step: 1 },
    { name: 'pp', label: 'PP size', type: 'number', default: 2, min: 1, max: 16, step: 1 },
    {
      name: 'variant',
      label: 'Variant',
      type: 'select',
      default: '3d',
      options: [
        { label: '3D (DP x TP x PP)', value: '3d' },
        { label: '5D (DP x TP x PP x EP x CP)', value: '5d' }
      ]
    }
  ],
  buildGraph: (params) => {
    const d_model = Number(params.d_model);
    const dp = Number(params.dp);
    const tp = Number(params.tp);
    const pp = Number(params.pp);
    const variant = String(params.variant);

    const g = new GraphBuilder();
    g.input('batch', [d_model]);

    if (variant === '3d') {
      // Standard 3D parallelism: DP x TP x PP
      g.replicate('dp', 'batch', [d_model], dp);
      g.split('tp', 'dp', [d_model / tp], 'tp');
      g.linear('compute', 'tp', [d_model / tp, d_model / tp]);
      g.allreduce('tp_sync', 'compute', [d_model / tp]);
      g.allgather('gather', 'tp_sync', [d_model]);
      g.p2p('pp', 'gather', [d_model]);
      g.scale('stages', 'pp', `${pp} stages`);
      g.allreduce('dp_sync', 'stages', [d_model]);
      g.output('out', 'dp_sync');
    } else {
      // 5D parallelism: DP x TP x PP x EP x CP
      // Data parallel replication
      g.replicate('dp', 'batch', [d_model], dp);

      // Context parallelism: split sequence
      g.split('cp', 'dp', [d_model], 'cp');

      // Tensor parallelism: split model dimension
      g.split('tp', 'cp', [d_model / tp], 'tp');
      g.linear('compute', 'tp', [d_model / tp, d_model / tp]);
      g.allreduce('tp_sync', 'compute', [d_model / tp]);
      g.allgather('tp_gather', 'tp_sync', [d_model]);

      // Expert parallelism: route tokens to experts
      g.router('moe_router', 'tp_gather', [d_model]);
      g.all2all('ep_dispatch', 'moe_router', [d_model]);
      g.linear('expert_compute', 'ep_dispatch', [d_model / tp, d_model / tp]);
      g.all2all('ep_combine', 'expert_compute', [d_model]);

      // Pipeline parallelism: P2P between stages
      g.p2p('pp', 'ep_combine', [d_model]);
      g.scale('stages', 'pp', `${pp} stages`);

      // Context parallel gather
      g.allgather('cp_gather', 'stages', [d_model]);

      // Data parallel sync
      g.allreduce('dp_sync', 'cp_gather', [d_model]);
      g.output('out', 'dp_sync');
    }

    return g.build();
  }
};
